import { Link } from 'react-router-dom'
import type { RefObject, CSSProperties } from 'react'
import type { ActionItem } from '../../types/ActionItem'
import '../../style/views/HomeView.css'

type NewContactCard = {
  id: string
  name: string
  company?: string
  createdLabel: string
}

type DefaultViewProps = {
  name?: string
  nameStyle?: CSSProperties
  typedNameRef: RefObject<HTMLSpanElement | null>
  latestActionItems: ActionItem[] | null
  newContacts: NewContactCard[] | null
  eventSummary: {
    today: Array<{ id: string; time: string; title: string; tagTheme: string }>
    weekCount: number
  } | null
  clientTotals: {
    newThisWeek: number
  } | null
  isStatsReady: boolean
  showWeeklyCallGradient: boolean
  weeklyCallDisplay: number
  utcStringToLocalDate: (utcString: string) => Date | null
}

const DefaultView = ({ 
  name, 
  nameStyle, 
  typedNameRef, 
  latestActionItems, 
  newContacts,
  eventSummary, 
  clientTotals, 
  isStatsReady, 
  showWeeklyCallGradient, 
  weeklyCallDisplay,
  utcStringToLocalDate
}: DefaultViewProps) => {
  return (
    <>
      <h1 className="home-title" style={{ marginBottom: 32, textAlign: 'center' }}>
        Welcome back
        {name ? (
          <>
            ,{' '}
            <span className="home-title-typed" style={nameStyle} ref={typedNameRef}>
              <span className="home-title-name">{name}</span>
              <span className="home-title-wave"> 👋</span>
            </span>
          </>
        ) : (
          null
        )}
      </h1>

      <div className="home-section">
        <p className="eyebrow">Action Items</p>
        <section className="home-panel home-stats" aria-label="Action items">
          <div className="home-action-items-list" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))' }}>
            {!latestActionItems ? (
              <>
                <div className="home-action-item is-loading">
                  <span className="home-loader" aria-hidden="true" />
                  <span>Loading action items...</span>
                </div>
                <div className="home-action-item is-loading">
                  <span className="home-loader" aria-hidden="true" />
                  <span>Loading action items...</span>
                </div>
              </>
            ) : latestActionItems.length ? (
              latestActionItems.map((item) => {
                const title = (item.item || '').trim() || 'Untitled action item'
                const dueDateValue = item.due_date || item.dueDate
                const dueDate = dueDateValue ? utcStringToLocalDate(dueDateValue) : null
                const dueDateLabel = dueDate
                  ? `${dueDate.toLocaleDateString([], { month: 'short', day: 'numeric' })} ${dueDate
                      .toLocaleTimeString([], {
                        hour: 'numeric',
                        minute: '2-digit',
                      })
                      .replace(/\s*([AP]M)$/i, (_, meridiem: string) => meridiem.toLowerCase())}`
                  : 'TBD'
                const startOfToday = new Date()
                startOfToday.setHours(0, 0, 0, 0)
                const dueDay = dueDate ? new Date(dueDate) : null
                if (dueDay) dueDay.setHours(0, 0, 0, 0)
                const twoDaysOut = new Date(startOfToday)
                twoDaysOut.setDate(startOfToday.getDate() + 2)
                const nowMs = Date.now()
                const dueStatus = !dueDate
                  ? 'default'
                  : dueDate.getTime() < nowMs
                    ? 'overdue'
                    : (dueDay?.getTime() ?? Number.MAX_SAFE_INTEGER) <= twoDaysOut.getTime()
                      ? 'approaching'
                      : 'default'
                return (
                  <Link key={item.id} className="home-action-item" to="/action-items" aria-label="Go to action items">
                    <span className="home-action-item-text">{title}</span>
                    <span className={`home-action-item-time due-date-pill is-${dueStatus}`}>{dueDateLabel || 'TBD'}</span>
                  </Link>
                )
              })
            ) : (
              <div className="home-action-item-empty">No action items yet</div>
            )}
          </div>
        </section>
      </div>

      <div className="home-section">
        <p className="eyebrow">Today</p>
        <section className="home-panel home-stats" aria-labelledby="home-highlights-title">
          <div className="home-stats-header">
            <h2 id="home-highlights-title" style={{ marginTop: 0 }}>Here's what's new</h2>
          </div>
          <div className="home-highlights-grid">
            <Link className="home-highlight-card" to="/calendar" aria-label="Go to calendar">
              <div className="home-highlight-header">
                <p className="eyebrow">Meetings</p>
                <span className="home-highlight-count">
                  {eventSummary ? eventSummary.today.length : <span className="home-loader" aria-label="Loading meetings" />}
                </span>
              </div>
              <h3 className="home-highlight-title">Today&apos;s meetings</h3>
              <div className="home-highlight-list">
                {!eventSummary ? (
                  <div className="home-highlight-item is-loading">
                    <span className="home-loader" aria-hidden="true" />
                    <span>Loading meetings...</span>
                  </div>
                ) : eventSummary.today.length ? (
                  eventSummary.today.slice(0, 3).map((event) => (
                    <div key={event.id} className={`home-highlight-item is-event-${event.tagTheme}`}>
                      <span className="home-highlight-meta">{event.time}</span>
                      <span className="home-highlight-text">{event.title}</span>
                    </div>
                  ))
                ) : (
                  <div className="home-highlight-empty">No meetings today</div>
                )}
              </div>
            </Link>

            <Link className="home-highlight-card" to="/contacts" aria-label="Go to contacts">
              <div className="home-highlight-header">
                <p className="eyebrow">Contacts</p>
                <span className="home-highlight-count">
                  {clientTotals ? clientTotals.newThisWeek : <span className="home-loader" aria-label="Loading contacts" />}
                </span>
              </div>
              <h3 className="home-highlight-title">New contacts</h3>
              <div className="home-highlight-list">
                {!newContacts ? (
                  <div className="home-highlight-item is-loading">
                    <span className="home-loader" aria-hidden="true" />
                    <span>Loading contacts...</span>
                  </div>
                ) : newContacts.length ? (
                  newContacts.slice(0, 3).map((contact) => (
                    <div key={contact.id} className="home-highlight-item">
                      <span className="home-highlight-meta">{contact.createdLabel}</span>
                      <span className="home-highlight-text">{contact.name}</span>
                    </div>
                  ))
                ) : (
                  <div className="home-highlight-empty">No new contacts this week</div>
                )}
              </div>
            </Link>
          </div>
        </section>
      </div>

      <div className="home-section">
        <p className="eyebrow">This Week</p>
        <section className="home-panel home-stats" aria-labelledby="home-weekly-stats-title">
          <div className="home-stats-header">
            {!isStatsReady ? <span className="home-loader" aria-label="Loading stats" /> : null}
          </div>
          <div className="home-kpi-grid">
            <div className="home-kpi-card">
              <span className="home-kpi-label">Call count</span>
              {!isStatsReady ? (
                <span className="home-loader" aria-label="Loading call count" />
              ) : (
                <span className={`home-kpi-value ${showWeeklyCallGradient ? 'gradient' : 'zero'}`}>
                  {weeklyCallDisplay}
                </span>
              )}
              <span className="home-kpi-note">Calls</span>
            </div>
            <div className="home-kpi-card">
              <span className="home-kpi-label">New clients</span>
              {!isStatsReady ? (
                <span className="home-loader" aria-label="Loading new clients" />
              ) : (
                <span className="home-kpi-value accent">{clientTotals?.newThisWeek || 0}</span>
              )}
              <span className="home-kpi-note">Added</span>
            </div>
            <div className="home-kpi-card">
              <span className="home-kpi-label">Events</span>
              {!isStatsReady ? (
                <span className="home-loader" aria-label="Loading events this week" />
              ) : (
                <span className="home-kpi-value">{eventSummary?.weekCount}</span>
              )}

              <span className="home-kpi-note">Scheduled</span>
            </div>
          </div>
        </section>
      </div>
    </>
  )
}

export default DefaultView
